import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { throwError, catchError } from 'rxjs';
import { AemService, CommonService, IEstimatedTotalRequestPayload, EnviormentConfig, SelectedModifierAndOptions, IModifierOption, IModifier, UserInfoService, ProductModel } from 'abs-ngx-library/src/lib/core';

@Injectable({
  providedIn: 'root'
})
export class CustomizeProductALV1Service {

  private config = EnviormentConfig;
  private currentEnv = '';
  public isCateringEnabled: boolean;

  constructor(private http: HttpClient, private _aemService: AemService, public _commonService: CommonService, public _userInfoService: UserInfoService) {
    this.currentEnv = this._commonService.getCurrentEnviorment();
    this.isCateringEnabled = !this._aemService.getFeatureFlagByNameDisable('isCateringDisabled');
  }

 
  /**
   * Sends a POST request to retrieve the estimated total based on the provided payload.
   *
   * @param requestPayload - The payload containing the necessary data to calculate the estimated total.
   * @returns An observable that emits the response from the API or an error if the request fails.
   *
   */
  public getEstimatedTotal(requestPayload: IEstimatedTotalRequestPayload) {

    const httpOptions = {
        headers: new HttpHeaders({
          'ocp-apim-subscription-key': this._aemService.getWcaxXapiApimKey()
        })
    };
    return this.http.post(`${this.config?.[this.currentEnv]?.catalogConfig?.['apim.api.catalog.path']}/product/estimatedTotal`, requestPayload, httpOptions).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    )
  };

  /**
   * Retrieves all selected modifier options from the provided mapping of modifiers and their selected options.
   *
   * @param selectedModifierAndOptions - An object where the keys are modifier names and the values are arrays of selected options for each modifier.
   * @returns An array of all selected modifier options.
   */
  public getAllModifierSelectedOptions(selectedModifierAndOptions: SelectedModifierAndOptions ): IModifierOption[] {
    if(!selectedModifierAndOptions) {
      return [];
    }
    
    const selectedOptionsPayload: IModifierOption[] = [];
    Object.keys(selectedModifierAndOptions)?.forEach(modifierName => {
      const options = selectedModifierAndOptions[modifierName] || [];
      options?.forEach(option => {
        if (this.isCateringEnabled) {
          // Preserve option object as provided. Only include qty when explicitly set as a number.
          const payloadOption: IModifierOption = { ...option } as IModifierOption;
          if (typeof option?.qty !== 'number') {
            delete (payloadOption as any).qty;
          }
          selectedOptionsPayload.push(payloadOption);
        } else {
          // Feature disabled: keep original behavior (push option as provided)
          selectedOptionsPayload.push(option);
        }
      });
    });

    return selectedOptionsPayload;
  }

  /**
   * Determines whether a given option is out of stock.
   *
   * @param option - The option to check for availability.
   * @returns A boolean value indicating whether the option is out of stock.
  */

  public isOutOfStock(option: any): boolean {
    const userChannelChoice = this._userInfoService.getUserChannel();
    return !this._commonService.checkItemAvailability_v2(option, userChannelChoice, false);
  }

  /**
   * Processes a list of modifiers to determine the initial selected options and their out-of-stock (OOS) status.
   * 
   * @param modifiers - An array of `IModifier` objects, each containing a list of options.
   * @returns An object mapping modifier names to their selected options. Each modifier name is associated with an array of selected options.
   * 
   * ### Behavior:
   * - For each modifier, iterates through its options to:
   *   - Set the `isSelected` property to `true` if the option is marked as default (`isDefault`).
   *   - Set the `isOOS` property based on the out-of-stock status of the option.
   * - If all default options for a modifier are out of stock, their `isSelected` property is set to `false`, and the defaults are cleared.
   * - If a modifier has exactly two options and all defaults are out of stock, the first available non-default, in-stock option is selected.
   */
  public getInitialSelectedModifierAndOptions(modifiers: IModifier[]): SelectedModifierAndOptions {
    const selectedModifierAndOptions: SelectedModifierAndOptions = {};

    modifiers?.forEach(modifier => {
      const options = modifier?.options || [];

      // Process options to set default selection and out-of-stock status
      options.forEach(option => {
        option.isSelected = false;
        option.isOOS = this.isOutOfStock(option);
        // Only select if default and not out of stock
        if (option.isDefault && !option.isOOS) {
          option.isSelected = true;
          // If catering is enabled and modifier has stepper enabled, ensure qty defaults to 1
          if (this.isCateringEnabled && modifier?.stepperEnabled && typeof option.qty !== 'number') {
            option.qty = 1;
          }
          (selectedModifierAndOptions[modifier.name] ||= []).push(option);
        }
      });

      if (options.length === 2) {
        const defaultOptions = options.filter(option => option.isDefault);
        const allDefaultsOOS = defaultOptions.length > 0 && defaultOptions.every(option => option.isOOS);
        if(allDefaultsOOS) {
          const firstAvailableOption = options.find(option => !option.isDefault && !option.isOOS);
          if (firstAvailableOption) {
            firstAvailableOption.isSelected = true;
            if (this.isCateringEnabled && modifier?.stepperEnabled && typeof firstAvailableOption.qty !== 'number') {
              firstAvailableOption.qty = 1;
            }
            selectedModifierAndOptions[modifier.name] = [firstAvailableOption];
          }
        }
      };

    });

    return selectedModifierAndOptions;
  };

   /**
   * Determines whether selected options meet the criteria.
   * @param modifiers - An array of `IModifier` objects, each containing a list of options to evaluate.
   * @param selectedModifierAndOptions - An object with selected modifiers.
   * @returns A boolean value indicating whether the selections are valid or not.
  */

  public isValidSelections(modifiers: IModifier[], selectedModifierAndOptions: any): boolean {
    return modifiers?.every(modifier => {
      const modifierOptions = selectedModifierAndOptions[modifier.name] || [];
      if (this.isCateringEnabled && modifier?.stepperEnabled){
        return this._commonService.isMinSelectSatisfied(modifier, modifierOptions)
      } else {
        return modifierOptions?.length >= modifier.minSelect;
      }
    });
  }

  /**
   * Determines whether give product is weighted product or not.
   * @param product - An array of `IModifier` objects, each containing a list of options to evaluate
   * @returns A boolean value indicating whether the product is weighted or not.
  */
  public isLBProduct(product: ProductModel): boolean {
    return product?.displayType === '3' && (product?.sellByWeight === 'W' || product?.sellByWeight === 'P')
  }

  /**
   * Determines whether give product is weighted product or not.
   * @param product - An array of `IModifier` objects, each containing a list of options to evaluate
   * @returns Returns the weighted.
  */
  public getItemPrice(product: ProductModel, quantity: number): string {
    if(this.isLBProduct(product)) {
      return product?.incrementPriceList?.find(
        item => +item.weight === +quantity
      )?.price || this._commonService.getValidProductPrice(product)?.toString();
    } else {
      return this._commonService.getValidProductPrice(product)?.toString()
    }
   
  }
  
  /**
   * Retrieves the pre-selected modifiers and their associated options based on the provided modifiers and selected options.
   *
   * @param modifiers - An array of modifiers, each containing a list of options.
   * @param selectedOptions - An array of selected options, each associated with a unique UPC ID.
   * @returns An object where the keys are modifier names and the values are arrays of selected options for each modifier.
   */
  public getPreSelectedModifierAndOptions(modifiers: IModifier[], selectedOptions: string[] | { upcId: string; qty?: number }[] = []): SelectedModifierAndOptions {
    const selectedModifierAndOptions: SelectedModifierAndOptions = {};

    if (!Array.isArray(modifiers)) return selectedModifierAndOptions;

    // Normalize selectedOptions so we can accept either string[] or {upcId, qty?}[]
    const normalizedSelectedOptions: { upcId: string; qty?: number }[] = Array.isArray(selectedOptions) && selectedOptions.length > 0
      ? (typeof selectedOptions[0] === 'string'
        ? (selectedOptions as string[]).map(s => ({ upcId: s }))
        : (selectedOptions as any[])
      )
      : [];

    const hasSelectedOptions = normalizedSelectedOptions.length > 0;

    modifiers?.forEach(modifier => {
      const selectedOptionsArray = selectedModifierAndOptions[modifier.name] || [];
      const options = modifier?.options || [];

      options.forEach(option => {
        option.isSelected = false;
        option.isOOS = this.isOutOfStock(option);

        // Try to find if current option is in selectedOptions list
        const selectedItem = hasSelectedOptions
          ? normalizedSelectedOptions.find(sel => sel.upcId === option.upcId)
          : null;

        const isSelectedByUpc = !!selectedItem;
        // Only consider qty selection when the feature flag and modifier stepper are enabled
        const isSelectedWithQty = this.isCateringEnabled && modifier.stepperEnabled && !!selectedItem?.qty && selectedItem.qty > 0;

        // Only preselect if not out of stock
        if ((isSelectedByUpc || isSelectedWithQty) && !option.isOOS) {
          option.isSelected = true;
          if (this.isCateringEnabled && modifier?.stepperEnabled) {
            // If the incoming selectedItem has a qty use it; otherwise default to 1 for stepper-enabled modifiers
            option.qty = typeof selectedItem?.qty === 'number' && selectedItem.qty > 0 ? selectedItem.qty : 1;
          } else {
            // Feature disabled: ensure qty is not set
            if ((option as any).qty) delete (option as any).qty;
          }
          selectedOptionsArray.push(option);
        } else if (!hasSelectedOptions && option.isDefault && !option.isOOS) {
          // If nothing selected, fall back to default option if not OOS
          option.isSelected = true;
          if (this.isCateringEnabled && modifier?.stepperEnabled && typeof option.qty !== 'number') {
            option.qty = 1;
          } else {
            if (!this.isCateringEnabled && (option as any).qty) delete (option as any).qty;
          }
          selectedOptionsArray.push(option);
        }
      });

      if (selectedOptionsArray.length > 0) {
        selectedModifierAndOptions[modifier.name] = selectedOptionsArray;
      }
    });

    return selectedModifierAndOptions;
  };

};
